import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, SectionTitle } from "../components/kit";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";

function Categories() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setRows(r.data)); }, []);
  return (
    <section className="bs-card p-6">
      <SectionTitle eyebrow="Product mix" title="Categories" />
      <div className="space-y-2" data-testid="categories-list">
        {rows.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3" data-testid={`category-${c.key}`}>
            <span className="font-semibold text-[#011E38]">{c.name}</span>
            <span className="text-xs text-slate-400">Fixed</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-3">BlueStone uses exactly these four categories everywhere: entries, returns, targets and reports. Category sales must add up to total sales.</p>
    </section>
  );
}

export default function Settings() {
  const { settings, reloadSettings } = useAuth();
  const [asOf, setAsOf] = useState(settings.as_of_date || "");
  const [mgr, setMgr] = useState(!!settings.manager_can_edit_targets);
  const [gi, setGi] = useState(settings.gms_default_instalments || 10);
  const [gw, setGw] = useState(settings.gms_upcoming_days || 30);

  const save = async (patch = {}) => {
    const body = { as_of_date: asOf || null, manager_can_edit_targets: mgr, gms_default_instalments: Number(gi) || 10, gms_upcoming_days: Number(gw) || 30, ...patch };
    try { await api.put("/settings", body); await reloadSettings(); toast.success("Settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="System" title="Settings" testid="settings-title" />
      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bs-card p-6 space-y-5">
          <SectionTitle eyebrow="Performance date" title={settings.as_of_date ? `Demo date: ${settings.as_of_date}` : "Live date"} />
          <p className="text-sm text-slate-500">All expected targets use working days before this date. Clear it to use the real current date (IST).</p>
          <div className="flex gap-2">
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-11 w-48" data-testid="as-of-date-input" />
            <Button onClick={() => save()} className="h-11 bg-[#011E38]" data-testid="save-as-of-button">Save</Button>
            <Button variant="outline" onClick={() => { setAsOf(""); save({ as_of_date: null }); }} className="h-11" data-testid="use-live-date-button">Use live date</Button>
          </div>
          <div className="text-sm">Effective date: <b data-testid="effective-date">{settings.effective_date}</b></div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-5">
            <div><div className="font-semibold text-[#011E38]">Managers can edit targets</div><div className="text-xs text-slate-500">Otherwise managers can only view targets.</div></div>
            <Switch checked={mgr} onCheckedChange={(v) => { setMgr(v); save({ manager_can_edit_targets: v }); }} data-testid="manager-targets-toggle" />
          </div>
        </section>
        <Categories />
        <section className="bs-card p-6 space-y-4" data-testid="gms-settings">
          <SectionTitle eyebrow="Gold Mine" title="GMS plan rules" />
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-sm font-semibold text-slate-600">Default number of instalments</span>
              <Input type="number" min="1" value={gi} onChange={(e) => setGi(e.target.value)} className="h-11 mt-1.5" data-testid="gms-default-instalments-input" /></label>
            <label className="block"><span className="text-sm font-semibold text-slate-600">Show upcoming payments (days ahead)</span>
              <Input type="number" min="1" value={gw} onChange={(e) => setGw(e.target.value)} className="h-11 mt-1.5" data-testid="gms-upcoming-days-input" /></label>
          </div>
          <p className="text-xs text-slate-500">Instalments are scheduled monthly from the plan start date. Staff can change the number of instalments per plan.</p>
          <Button onClick={() => save()} className="h-11 bg-[#011E38]" data-testid="save-gms-settings-button">Save GMS rules</Button>
        </section>
      </div>
    </div>
  );
}
